// Portal app shell — reuses the .app/.sidebar/.main design classes verbatim.
import { h, icon, brandLogo } from './dom.js';
import { router } from '../router/router.js';
import { api } from '../api/client.js';
import { config } from '../config.js';
import { logoutLocal } from '../api/http.js';
import { tzShortLabel } from '../lib/timezone.js';
import { NAV, SIGN_OUT_LABEL, approvalsLabelText } from './nav.js';
import { mountJoinBanner, unmountJoinBanner } from './join-banner.js';
import { mountApprovalsBanner, unmountApprovalsBanner } from './approvals-banner.js';

export { sidebarItems } from './nav.js';

// Cached current-user handle so the brand username fills without flicker across
// route changes. Populated once per page load; refreshed lazily.
let cachedHandle: string | null = null;
let handleFetch: Promise<string | null> | null = null;
function currentHandle(): Promise<string | null> {
  if (cachedHandle) return Promise.resolve(cachedHandle);
  if (!handleFetch) {
    handleFetch = api.me()
      .then((me) => { cachedHandle = me?.handle || me?.username || null; return cachedHandle; })
      .catch(() => null);
  }
  return handleFetch;
}

const ICONS = {
  logo: '<rect x="3" y="4" width="18" height="16" rx="3"/><circle cx="9" cy="11" r="2.4"/><path d="M14 9h4M14 13h4M6 16.5c.8-1.6 4.2-1.6 5 0"/>',
  dashboard: '<rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/>',
  profiles: '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-6 8-6s8 2 8 6"/>',
  fields: '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3.5" cy="6" r="1.2"/><circle cx="3.5" cy="12" r="1.2"/><circle cx="3.5" cy="18" r="1.2"/>',
  links: '<path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1"/><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1"/>',
  subscribers: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/>',
  contacts: '<path d="M20 21v-2a4 4 0 0 0-3-3.87M4 21v-2a4 4 0 0 1 3-3.87"/><circle cx="12" cy="7" r="4"/>',
  approvals: '<path d="M9 12l2 2 4-4"/><path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/>',
  code: '<rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/><circle cx="12" cy="16" r="1.4"/>',
  settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
  menu: '<line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>',
  close: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
} as const;

const NAV_ICON_MAP: Record<string, keyof typeof ICONS> = {
  dashboard: 'dashboard', profiles: 'profiles', fields: 'fields', links: 'links',
  subscribers: 'subscribers', contacts: 'contacts', code: 'code', settings: 'settings', approvals: 'approvals',
};

function sideLink(item: (typeof NAV)[number], active: boolean, onNavigate?: () => void): HTMLElement {
  const label = item.icon === 'approvals'
    ? h('span', { class: 'side-link-label', 'data-approvals-label': '1' }, item.label)
    : item.label;
  const a = h('a', {
    class: 'side-link' + (active ? ' active' : ''), href: '#' + item.path,
    onclick: onNavigate ? () => onNavigate() : undefined,
  }, icon(ICONS[NAV_ICON_MAP[item.icon]]), label);
  return a;
}

// approvalsLabelText renders "Approvals" or "Approvals (N)" for N>0. Kept pure so
// it is unit-testable and shared by desktop sidebar + mobile drawer badges.
export { approvalsLabelText } from './nav.js';

// refreshApprovalsBadge fetches the pending count and updates every rendered
// approvals label in the chrome (sidebar + drawer).
async function refreshApprovalsBadge(): Promise<void> {
  const count = await api.approvalsCount().catch(() => 0);
  const text = approvalsLabelText(count);
  document.querySelectorAll('[data-approvals-label]').forEach((el) => { el.textContent = text; });
}

// Chrome-lifetime poller: refresh the approvals badge every 3s while authed
// chrome is mounted. Reset on each renderShell so we never stack timers.
let approvalsPoll: ReturnType<typeof setInterval> | null = null;
function startApprovalsPoll(): void {
  if (approvalsPoll) clearInterval(approvalsPoll);
  void refreshApprovalsBadge();
  approvalsPoll = setInterval(() => { void refreshApprovalsBadge(); }, 3000);
}

async function doSignOut(): Promise<void> {
  try { await api.auth.logout(); } catch { /* best-effort */ }
  unmountJoinBanner();
  unmountApprovalsBanner();
  logoutLocal();
  router.navigate('/login');
}

/**
 * Build the shared sidebar CONTENT (nav links + divider + Sign out), reused by
 * both the desktop sidebar and the mobile drawer. `onNavigate` fires after a
 * link/sign-out is chosen (used to close the mobile drawer).
 */
export function sidebarContent(activePath: string, onNavigate?: () => void): HTMLElement[] {
  return [
    ...NAV.map((n) => sideLink(n, n.path === activePath || (n.path !== '/' && activePath.startsWith(n.path)), onNavigate)),
    h('hr', { class: 'divider' }),
    h('a', {
      class: 'side-link', href: '#/login', style: 'cursor:pointer', 'data-signout': '1',
      onclick: async (e: Event) => {
        e.preventDefault();
        onNavigate?.();
        await doSignOut();
      },
    }, SIGN_OUT_LABEL),
    versionFooter(),
  ];
}

// Subtle version footer pinned at the bottom of the menu: shows the App version
// immediately (build-time injected) and fills in the API version async from the
// backend /version endpoint. Deliberately low-contrast so it doesn't grab
// attention. On mobile it rides along at the bottom of the drawer (there's no
// separate page footer to clutter).
export function versionFooter(): HTMLElement {
  const apiSpan = h('span', {}, 'API …');
  const el = h('div', { class: 'side-version', 'aria-label': 'Version information' },
    h('span', {}, 'Timezone: ' + tzShortLabel()),
    h('br', { 'aria-hidden': 'true' }),
    h('span', {}, 'App v' + config.appVersion),
    h('br', { 'aria-hidden': 'true' }),
    apiSpan);
  // Best-effort: leave "API …" -> "API n/a" if the endpoint is unreachable.
  void api.version()
    .then((v) => {
      // The API may report a full SHA as its "version"; show a short form so the
      // footer stays compact. A real semver (e.g. 1.2.3) is shown as-is.
      const raw = v.version || 'unknown';
      const short = /^[0-9a-f]{12,40}$/i.test(raw) ? raw.slice(0, 7) : raw;
      apiSpan.textContent = 'API v' + short;
      if (v.commit && v.commit !== 'unknown') el.title = 'API commit ' + v.commit + (v.build_date && v.build_date !== 'unknown' ? ' • ' + v.build_date : '');
    })
    .catch(() => { apiSpan.textContent = 'API n/a'; });
  return el;
}

/** Render the portal chrome and return the .main content container to fill. */
export function renderShell(activePath: string): HTMLElement {
  const app = document.getElementById('app')!;
  const main = h('main', { class: 'main' });

  // Brand block: logo/wordmark on top, current username underneath so people
  // can see which identity they're signed in as. Username fills in async.
  const brand = () => {
    const user = h('small', { class: 'brand-user' });
    void currentHandle().then((hnd) => { if (hnd) user.textContent = '@' + hnd; });
    return h('div', { class: 'brand-block' },
      h('a', { class: 'brand', href: '#/' }, brandLogo(26), 'VirtualID'),
      user,
    );
  };

  // Desktop sidebar (hidden <=860px via CSS).
  const sidebar = h('aside', { class: 'sidebar' }, brand(), ...sidebarContent(activePath));

  // Mobile drawer + scrim (hidden >860px via CSS).
  const closeDrawer = () => { drawer.classList.remove('open'); scrim.classList.remove('open'); document.body.classList.remove('drawer-open'); };
  const openDrawer = () => { drawer.classList.add('open'); scrim.classList.add('open'); document.body.classList.add('drawer-open'); };
  const scrim = h('div', { class: 'drawer-scrim', 'aria-hidden': 'true', onclick: closeDrawer });
  const drawer = h('aside', { class: 'sidebar drawer', id: 'mobile-drawer' },
    h('button', { class: 'drawer-close', type: 'button', 'aria-label': 'Close menu', onclick: closeDrawer }, icon(ICONS.close, 20)),
    brand(),
    ...sidebarContent(activePath, closeDrawer),
  );

  // Mobile top app bar (hidden >860px via CSS).
  const topbar = h('header', { class: 'topbar' },
    h('button', { class: 'hamburger', type: 'button', 'aria-label': 'Open menu', 'aria-controls': 'mobile-drawer', onclick: openDrawer }, icon(ICONS.menu, 22)),
    h('a', { class: 'brand', href: '#/' }, brandLogo(24), 'VirtualID'),
  );

  app.replaceChildren(h('div', { class: 'app' }, sidebar, topbar, scrim, drawer, main));
  // Mount the global device-join approval banner (authed chrome only).
  mountJoinBanner();
  // Mount the explicit subscription-approvals banner (authed chrome only).
  mountApprovalsBanner();
  // Kick the approvals badge poll (updates "Approvals (N)" every 3s).
  startApprovalsPoll();
  return main;
}

export function pageHead(title: string, subtitle?: string, action?: HTMLElement): HTMLElement {
  return h('div', { class: 'page-head row between' },
    h('div', {},
      h('h1', { style: 'font-size:1.7rem;margin:0' }, title),
      subtitle ? h('p', { style: 'margin:0' }, subtitle) : null,
    ),
    action || null,
  );
}

export function portalFooter(): HTMLElement {
  return h('footer', { class: 'portal-footer' },
    h('span', { html: '© 2026 VirtualID · <a href="https://virtualid.one/">virtualid.one</a>' }),
    h('span', {}, 'One Identity, One You, One Virtual ID.'),
  );
}

// renderPublicShell renders the PUBLIC (logged-out-friendly) chrome used by
// unauthenticated surfaces — public profiles and invites. It mirrors the
// marketing site template (mockups/public-profile.html): a `.site-header` with
// brand + Features/Pricing/Sign in/Get started, a centered `<main>`, and the
// `.site-footer`. Crucially it has NO app sidebar/menu, so logged-out visitors
// never see owner navigation. Returns the centered content container to fill.
export function renderPublicShell(): HTMLElement {
  const app = document.getElementById('app')!;
  const content = h('section', { class: 'section container', style: 'max-width:560px' });

  // Marketing links (brand, Features, Pricing) point to the MAIN marketing site
  // (virtualid.one), NOT the app host these public/invite pages are served from.
  // Auth CTAs (Sign in, Get started) stay on the app (app.virtualid.one).
  const MARKETING = 'https://virtualid.one';
  const APP = 'https://app.virtualid.one';
  const header = h('header', { class: 'site-header' },
    h('div', { class: 'container nav' },
      h('a', { class: 'brand', href: MARKETING + '/' }, brandLogo(26), 'VirtualID'),
      h('nav', { class: 'nav-links' },
        h('a', { class: 'nav-collapse', href: MARKETING + '/#features' }, 'Features'),
        h('a', { class: 'nav-collapse', href: MARKETING + '/#pricing' }, 'Pricing'),
        h('a', { href: APP + '/#/login' }, 'Sign in'),
        h('a', { class: 'btn btn-primary btn-sm', href: APP + '/#/login' }, 'Get started'),
      )));

  const footer = h('footer', { class: 'site-footer' },
    h('div', { class: 'container' },
      h('div', { class: 'row between' },
        h('small', {}, '© 2026 VirtualID · virtualid.one'),
        h('small', {}, 'Privacy-first · GDPR by design'))));

  app.replaceChildren(header, h('main', {}, content), footer);
  // Public surfaces must never show the trusted-device approval banner.
  unmountJoinBanner();
  return content;
}
